import { useState } from 'react'
import { Shuffle, Wand2, Undo2, AlertTriangle, CheckCircle2 } from 'lucide-react'
import toast from 'react-hot-toast'
import carteraService from '@/services/carteraService'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/**
 * Reasignar el catálogo de cuentas de un cliente que YA tiene transacciones.
 *
 * El asesor hace por el cliente lo mismo que el dueño puede hacer desde su escritorio, con el
 * MISMO motor: la validación, las siete invariantes y el delta log corren en el tenant y
 * dentro de una transacción. Este componente no calcula nada — y no debe, porque un remap
 * decidido en el portal y aplicado por partes en el tenant es justo el fallo que el diseño
 * evita.
 *
 * A diferencia de los datos de prueba, esto SÍ tiene sentido en una empresa real: es para
 * ella. El orden de la operación es:
 *
 *   1. El contador trae su catálogo con el asistente de migración (sección «Plan de cuentas»).
 *      Sus cuentas entran marcadas y conviven con las del estándar.
 *   2. Aquí se propone el mapeo solo, ordenado POR IMPORTE — no por código: revisando diez
 *      filas queda cubierto el 95 % del dinero.
 *   3. Se previsualiza (balanza antes/después) y se aplica.
 */
export default function RemapCuentas({ subId }) {
  const [diag, setDiag] = useState(null)
  const [remaps, setRemaps] = useState([])
  const [sugerencias, setSugerencias] = useState([])
  const [destino, setDestino] = useState({})
  const [marcadas, setMarcadas] = useState({})
  const [previa, setPrevia] = useState(null)
  const [resultado, setResultado] = useState(null)
  const [permitirTipo, setPermitirTipo] = useState(false)
  const [confirmacion, setConfirmacion] = useState('')
  const [trabajando, setTrabajando] = useState(false)

  const fallo = (e, porDefecto) => toast.error(e?.response?.data?.error || porDefecto)

  const reglas = sugerencias
    .filter((s) => marcadas[s.desde] && destino[s.desde])
    .map((s) => ({ desde: s.desde, hacia: destino[s.desde] }))

  // Fricción proporcional: una recodificación 1→1 sobre período abierto no merece ceremonia;
  // fusionar cuentas con historia o tocar un ejercicio cerrado, sí.
  const hayFusion = (() => {
    const cuenta = new Map()
    for (const r of reglas) cuenta.set(r.hacia, (cuenta.get(r.hacia) || 0) + 1)
    return [...cuenta.values()].some((n) => n > 1)
  })()
  const exigeConfirmar = hayFusion || (previa?.ejerciciosReclasificados?.length || 0) > 0

  const cargar = async () => {
    setTrabajando(true)
    try {
      const r = await carteraService.diagnosticoRemap(subId)
      setDiag(r?.data || null)
      setRemaps(r?.data?.remaps || [])
    } catch (e) { fallo(e, 'No se pudo leer el catálogo del cliente') } finally { setTrabajando(false) }
  }

  const proponer = async () => {
    setTrabajando(true)
    try {
      const r = await carteraService.proponerRemap(subId)
      const lista = r?.data?.sugerencias || []
      setSugerencias(lista)
      setDestino(Object.fromEntries(lista.map((s) => [s.desde, s.hacia || ''])))
      setMarcadas(Object.fromEntries(lista.map((s) => [s.desde, !!(s.marcada && s.hacia)])))
      setPrevia(null)
      setResultado(null)
    } catch (e) { fallo(e, 'No se pudo proponer el mapeo') } finally { setTrabajando(false) }
  }

  const previsualizar = async () => {
    setTrabajando(true)
    try {
      const r = await carteraService.remapCuentas(subId, { reglas, permitirReclasificacionDeTipo: permitirTipo })
      setPrevia(r?.data || null)
      setResultado(null)
    } catch (e) {
      // El 409 trae los BLOQUEOS: qué mapeo no se puede hacer y por qué. Es el resultado
      // que el asesor tiene que leer, no un fallo que ocultar tras un toast genérico.
      const d = e?.response?.data
      if (d?.bloqueos?.length) setPrevia({ ok: false, bloqueos: d.bloqueos, avisos: d.avisos || [], balanza: [] })
      fallo(e, 'No se pudo previsualizar')
    } finally { setTrabajando(false) }
  }

  const aplicar = async () => {
    setTrabajando(true)
    try {
      const r = await carteraService.remapCuentas(
        subId,
        { reglas, permitirReclasificacionDeTipo: permitirTipo, descripcion: 'Cambio de catálogo (asesor)' },
        { aplicar: true },
      )
      setResultado(r?.data || null)
      toast.success(`Listo: ${r?.data?.filasTocadas || 0} fila(s) reasignadas.`)
      await cargar()
    } catch (e) {
      // Las invariantes que no pasan llegan aquí: no se escribió NADA (hubo ROLLBACK) y lo
      // que hay que enseñar es cuál falló, renglón a renglón.
      const d = e?.response?.data
      if (d?.fallos?.length) setResultado({ ok: false, fallos: d.fallos, error: d.error })
      fallo(e, 'No se pudo reasignar')
    } finally { setTrabajando(false) }
  }

  const deshacer = async (remapId) => {
    setTrabajando(true)
    try {
      // Previsualizar SIEMPRE antes: si el negocio ya asentó con los códigos nuevos,
      // deshacer dejaría los libros en dos codificaciones y el servidor lo bloquea.
      await carteraService.deshacerRemap(subId, remapId, { previsualizar: true })
      await carteraService.deshacerRemap(subId, remapId)
      toast.success('Reasignación deshecha.')
      await cargar()
    } catch (e) {
      const h = e?.response?.data?.huerfanosPosteriores || []
      if (h.length) {
        toast.error(
          `No se puede deshacer: ya hay movimiento nuevo (${h.map((x) => `${x.codigo}: ${x.lineas}`).join(', ')}). ` +
          'Hacé una reasignación en sentido contrario.',
        )
      } else fallo(e, 'No se pudo deshacer')
    } finally { setTrabajando(false) }
  }

  return (
    <Card className="p-4 sm:p-5 space-y-3">
      <div>
        <h3 className="font-semibold text-neutral-900 dark:text-neutral-50 flex items-center gap-2">
          <Shuffle size={16} /> Cambiar su catálogo de cuentas
        </h3>
        <p className="text-caption text-neutral-500 dark:text-neutral-400">
          Para el cliente que ya lleva meses facturando y cuyo contador quiere su propio plan de
          cuentas. Se mueve toda la historia contable sin perder un asiento: nada se escribe hasta
          que veas la balanza antes y después, y todo queda registrado para poder deshacerlo.
        </p>
        <p className="text-caption text-neutral-500 dark:text-neutral-400">
          Primero traé el catálogo del contador con el asistente de migración (sección «Plan de
          cuentas»). Aquí se compara ese contra el que la empresa venía usando.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={cargar} disabled={trabajando}>Ver su catálogo</Button>
        <Button onClick={proponer} disabled={trabajando || !diag}>
          <Wand2 size={15} className="mr-1.5" /> Proponer el mapeo
        </Button>
      </div>

      {diag && (
        <p className="text-body-sm text-neutral-700 dark:text-neutral-300 border-t border-neutral-100 dark:border-neutral-800 pt-3">
          <strong>{diag.totalCuentas}</strong> cuentas; <strong>{diag.conMovimiento}</strong> con
          movimiento ({diag.lineas} partidas
          {diag.primeraFecha ? `, del ${diag.primeraFecha} al ${diag.ultimaFecha}` : ''}).{' '}
          {diag.ejerciciosCerrados?.length
            ? `Ejercicios cerrados: ${diag.ejerciciosCerrados.join(', ')} — no se reexpresan, se saldan con un asiento de reclasificación.`
            : 'Ningún ejercicio cerrado.'}
        </p>
      )}

      {sugerencias.length > 0 && (
        <div className="border-t border-neutral-100 dark:border-neutral-800 pt-3 space-y-2">
          <p className="text-caption text-neutral-500 dark:text-neutral-400">
            {reglas.length} de {sugerencias.length} mapeadas. Ordenadas por importe: las de arriba
            son las que mueven el dinero. Lo que llega por parecido de nombre viene DESMARCADO.
          </p>
          <div className="max-h-80 overflow-auto rounded-lg border border-neutral-100 dark:border-neutral-800">
            <table className="w-full text-caption">
              <thead className="sticky top-0 bg-neutral-50 dark:bg-neutral-800/60">
                <tr className="text-left text-neutral-500 dark:text-neutral-400">
                  <th className="p-2"> </th>
                  <th className="p-2">Cuenta actual</th>
                  <th className="p-2">Pasa a</th>
                  <th className="p-2">Por qué</th>
                </tr>
              </thead>
              <tbody>
                {sugerencias.map((s) => (
                  <tr key={s.desde} className="border-t border-neutral-100 dark:border-neutral-800">
                    <td className="p-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={!!marcadas[s.desde]}
                        onChange={(e) => setMarcadas((m) => ({ ...m, [s.desde]: e.target.checked }))}
                      />
                    </td>
                    <td className="p-2 font-mono text-neutral-800 dark:text-neutral-200">{s.desde}</td>
                    <td className="p-2">
                      <select
                        value={destino[s.desde] || ''}
                        onChange={(e) => setDestino((d) => ({ ...d, [s.desde]: e.target.value }))}
                        className="h-9 w-full min-w-[10rem] rounded border border-line bg-card px-2 text-foreground"
                      >
                        <option value="">— sin destino —</option>
                        {(diag?.cuentas || []).map((c) => (
                          <option key={c.codigo} value={c.codigo}>{c.codigo} · {c.nombre}</option>
                        ))}
                      </select>
                    </td>
                    <td className={`p-2 ${s.confianza === 'similitud' ? 'text-warning-700 dark:text-warning-300' : 'text-neutral-500 dark:text-neutral-400'}`}>
                      {s.motivo}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <label className="flex min-h-10 items-center gap-2 text-caption text-neutral-500 dark:text-neutral-400">
            <input type="checkbox" className="h-4 w-4 shrink-0 accent-primary" checked={permitirTipo} onChange={(e) => setPermitirTipo(e.target.checked)} />
            El contador reclasifica a propósito (algo que llevaba como gasto ahora es costo)
          </label>
          <Button variant="outline" onClick={previsualizar} disabled={trabajando || reglas.length === 0}>
            Ver qué pasaría
          </Button>
        </div>
      )}

      {previa && (
        <div className="border-t border-neutral-100 dark:border-neutral-800 pt-3 space-y-2">
          {previa.bloqueos?.length > 0 && (
            <div className="rounded-lg border border-danger-200 dark:border-danger-800 bg-danger-50 dark:bg-danger/15 p-3 text-body-sm text-danger-700 dark:text-danger-300">
              <p className="font-semibold flex items-center gap-1.5"><AlertTriangle size={14} /> No se puede aplicar:</p>
              <ul className="list-disc pl-5 mt-1">{previa.bloqueos.map((b, i) => <li key={i}>{b.texto}</li>)}</ul>
            </div>
          )}
          {previa.avisos?.length > 0 && (
            <div className="rounded-lg border border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning/15 p-3 text-body-sm text-warning-700 dark:text-warning-300">
              <ul className="list-disc pl-5">{previa.avisos.map((a, i) => <li key={i}>{a.texto}</li>)}</ul>
            </div>
          )}
          {previa.balanza?.length > 0 && (
            <>
              <p className="text-caption text-neutral-500 dark:text-neutral-400">
                Balanza antes y después — {previa.totalFilas} fila(s) en {previa.porTabla?.length || 0} tabla(s).
                {previa.ejerciciosReclasificados?.length
                  ? ` Se saldan con asiento de reclasificación: ${previa.ejerciciosReclasificados.join(', ')}.`
                  : ''}
              </p>
              <div className="max-h-72 overflow-auto rounded-lg border border-neutral-100 dark:border-neutral-800">
                <table className="w-full text-caption">
                  <thead className="sticky top-0 bg-neutral-50 dark:bg-neutral-800/60">
                    <tr className="text-left text-neutral-500 dark:text-neutral-400">
                      <th className="p-2">Código</th><th className="p-2">Cuenta</th>
                      <th className="p-2 text-right">Saldo antes</th>
                      <th className="p-2">Pasa a</th>
                      <th className="p-2 text-right">Saldo después</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previa.balanza.map((b) => (
                      <tr key={b.codigo} className="border-t border-neutral-100 dark:border-neutral-800">
                        <td className="p-2 font-mono">{b.codigo}</td>
                        <td className="p-2">{b.nombre}</td>
                        <td className="p-2 text-right">{Number(b.saldoAntes).toFixed(2)}</td>
                        <td className="p-2 font-mono">{b.codigoDespues || '— baja —'}</td>
                        <td className="p-2 text-right">{Number(b.saldoDespues).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {previa.ok && (
            <div className="flex flex-wrap items-center gap-2">
              {exigeConfirmar && (
                <>
                  <span className="text-caption text-neutral-600 dark:text-neutral-400">
                    Fusiona cuentas o toca un ejercicio cerrado: escribí CONFIRMO.
                  </span>
                  <Input
                    value={confirmacion}
                    onChange={(e) => setConfirmacion(e.target.value)}
                    placeholder="CONFIRMO"
                    className="w-40"
                  />
                </>
              )}
              <Button
                onClick={aplicar}
                disabled={trabajando || (exigeConfirmar && confirmacion.trim().toUpperCase() !== 'CONFIRMO')}
              >
                Aplicar la reasignación
              </Button>
            </div>
          )}
        </div>
      )}

      {resultado && (
        <div className="border-t border-neutral-100 dark:border-neutral-800 pt-3">
          {resultado.ok ? (
            <p className="text-body-sm text-success-700 dark:text-success-300 flex items-start gap-1.5">
              <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
              Listo: {resultado.filasTocadas} fila(s) reasignadas
              {resultado.asientoReclasificacionId ? ', con asiento de reclasificación para los ejercicios cerrados.' : '.'}
            </p>
          ) : (
            <div className="text-body-sm text-danger-700 dark:text-danger-300">
              <p className="font-semibold">No se aplicó nada: las comprobaciones de integridad no pasaron.</p>
              <ul className="list-disc pl-5 mt-1">
                {(resultado.fallos || []).map((f, i) => <li key={i}><strong>{f.id} · {f.titulo}</strong> — {f.detalle}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {remaps.length > 0 && (
        <div className="border-t border-neutral-100 dark:border-neutral-800 pt-3">
          <p className="text-caption text-neutral-500 dark:text-neutral-400 mb-1">Reasignaciones anteriores</p>
          {remaps.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 border-b border-neutral-100 dark:border-neutral-800 py-2 text-caption">
              <span className="font-mono text-neutral-500 dark:text-neutral-400">{String(r.createdAt).slice(0, 16)}</span>
              <span className="text-neutral-800 dark:text-neutral-200">{r.reglas?.length || 0} cuenta(s) · {r.filasTocadas} fila(s)</span>
              <span className={r.estado === 'aplicado' ? 'text-success-700 dark:text-success-300' : r.estado === 'fallido' ? 'text-danger-700 dark:text-danger-300' : 'text-neutral-500 dark:text-neutral-400'}>
                {r.estado}
              </span>
              {r.estado === 'aplicado' && (
                <Button variant="ghost" size="sm" className="ml-auto" onClick={() => deshacer(r.id)} disabled={trabajando}>
                  <Undo2 size={13} className="mr-1.5" /> Deshacer
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
